import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Award, RefreshCw, Upload, FileText, Eye, Download, AlertCircle } from 'lucide-react';
import { employeeLicenseApi } from '../services/api';
import { EmployeeLicense } from '../types';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { formatCompanyDate } from '../utils/companyDateFormat';
import CertificatePreviewModal from '../components/CertificatePreviewModal';
import { triggerBlobDownload } from '../utils/certificateFiles';
import {
  getLicenseExpiryStatus,
  HEALTHCARE_LICENSE_TYPES,
  LICENSE_STATUS_STYLES,
} from '../constants/healthcareLicenseTypes';

const MyLicenses: React.FC = () => {
  const { employee } = useAuth();
  const { healthcareComplianceEnabled } = usePermissions();
  const [licenses, setLicenses] = useState<EmployeeLicense[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [uploadingId, setUploadingId] = useState<number | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [previewLicense, setPreviewLicense] = useState<EmployeeLicense | null>(null);
  const [selectedLicense, setSelectedLicense] = useState<EmployeeLicense | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const pageTitle = healthcareComplianceEnabled ? 'My Compliance & Renewals' : 'My Licenses';
  const pageSubtitle = healthcareComplianceEnabled
    ? 'Track professional certificates, upload renewals, and stay ahead of expiry reminders.'
    : 'View your license records and expiry dates.';

  const fetchLicenses = useCallback(async () => {
    if (!employee?.id) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await employeeLicenseApi.getByEmployeeId(employee.id);
      const list = Array.isArray(data) ? [...data] : [];
      list.sort((a, b) => new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime());
      setLicenses(list);
    } catch {
      setLicenses([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [employee?.id]);

  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchLicenses();
  };

  const triggerUpload = (license: EmployeeLicense) => {
    setSelectedLicense(license);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !selectedLicense) return;

    try {
      setUploadingId(selectedLicense.id);
      await employeeLicenseApi.uploadCertificate(selectedLicense.id, file);
      await fetchLicenses();
    } catch (err: any) {
      alert(err?.message || 'Failed to upload certificate.');
    } finally {
      setUploadingId(null);
      setSelectedLicense(null);
    }
  };

  const downloadCertificate = async (license: EmployeeLicense) => {
    try {
      setDownloadingId(license.id);
      const blob = await employeeLicenseApi.downloadCertificate(license.id, 'attachment');
      triggerBlobDownload(blob, license.certificateFileName || `${license.licenseType || 'certificate'}.pdf`);
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || 'Could not download certificate.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
        className="hidden"
        onChange={handleFileSelected}
      />

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Award className="h-7 w-7 text-blue-600" />
            <h1 className="text-2xl font-bold text-neutral-800">{pageTitle}</h1>
          </div>
          <p className="text-sm text-neutral-600 mt-2 max-w-2xl">{pageSubtitle}</p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 self-start"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {healthcareComplianceEnabled && licenses.some((l) => getLicenseExpiryStatus(l.expiryDate).tone === 'urgent') && (
        <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4">
          <AlertCircle className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-orange-900">Renewal action needed</p>
            <p className="text-sm text-orange-800 mt-1">
              One or more certificates expire within 30 days. Upload your renewed document below — your supervisor will
              be notified automatically.
            </p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64 text-neutral-500">Loading certificates...</div>
      ) : licenses.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Award className="h-16 w-16 text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-500">No certificates on your profile yet.</p>
          <p className="text-sm text-neutral-400 mt-2">Contact your administrator if you need a record added.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {licenses.map((license) => {
            const status = getLicenseExpiryStatus(license.expiryDate);
            const styles = LICENSE_STATUS_STYLES[status.tone];
            const showUpload = status.tone === 'urgent' || status.tone === 'expired' || status.tone === 'warning';
            return (
              <div
                key={license.id}
                className={`rounded-xl border ${styles.border} ${styles.bg} p-5 shadow-sm`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h2 className="text-lg font-semibold text-neutral-900">
                        {license.licenseType || 'License'}
                      </h2>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${styles.badge}`}>
                        {status.label}
                      </span>
                      {license.certificateUploadedAt && (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                          Renewal uploaded
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-neutral-700">#{license.licenseNumber}</p>
                    <p className="text-sm text-neutral-600 mt-1">
                      Expires {formatCompanyDate(license.expiryDate)}
                    </p>
                    {license.certificateFileName && (
                      <p className="text-xs text-neutral-500 mt-2 flex items-center gap-1">
                        <FileText className="h-3.5 w-3.5" />
                        {license.certificateFileName}
                        {license.certificateUploadedAt && (
                          <span> · uploaded {formatCompanyDate(license.certificateUploadedAt)}</span>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    {license.certificateFileKey && (
                      <>
                        <button
                          type="button"
                          onClick={() => setPreviewLicense(license)}
                          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white hover:bg-neutral-50"
                        >
                          <Eye className="h-4 w-4" />
                          Preview
                        </button>
                        <button
                          type="button"
                          disabled={downloadingId === license.id}
                          onClick={() => void downloadCertificate(license)}
                          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white hover:bg-neutral-50 disabled:opacity-50"
                        >
                          <Download className="h-4 w-4" />
                          {downloadingId === license.id ? 'Downloading…' : 'Download'}
                        </button>
                      </>
                    )}
                    {showUpload && (
                      <button
                        type="button"
                        disabled={uploadingId === license.id}
                        onClick={() => triggerUpload(license)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        <Upload className="h-4 w-4" />
                        {uploadingId === license.id ? 'Uploading…' : 'Upload renewal'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {healthcareComplianceEnabled && (
        <p className="text-xs text-neutral-500">
          Supported document types: {HEALTHCARE_LICENSE_TYPES.join(', ')}. Upload PDF or image (max 10 MB).
        </p>
      )}

      <CertificatePreviewModal
        licenseId={previewLicense?.id ?? null}
        fileName={previewLicense?.certificateFileName}
        title={previewLicense?.licenseType || undefined}
        onClose={() => setPreviewLicense(null)}
      />
    </div>
  );
};

export default MyLicenses;
